import { useEffect, useRef, useState } from 'react';
import { SUPPORT, lienWhatsapp, lienTelephone } from '@/config/support';

/**
 * Le bouton d'aide, en bas à droite de l'écran.
 *
 * Deux canaux, dans l'ordre où les gens les utilisent ici : WhatsApp d'abord
 * — c'est là que les clients d'Abidjan écrivent, pas dans un formulaire —,
 * puis l'appel. L'e-mail s'ajoute tout seul le jour où une adresse est
 * renseignée dans src/config/support.ts.
 *
 * Il ne remplace pas la messagerie de la plateforme : celle-ci sert à parler à
 * une agence ou à un vendeur à propos d'un véhicule précis. Ce bouton-ci sert
 * à joindre BD Innovation, quand quelque chose ne va pas.
 *
 * Il se range sous le menu mobile (z-30 contre z-40 pour l'en-tête) : un
 * bouton d'aide n'a aucune raison de passer par-dessus la navigation ouverte.
 */
export function SupportButton() {
  const [ouvert, setOuvert] = useState(false);
  const zone = useRef<HTMLDivElement>(null);
  const declencheur = useRef<HTMLButtonElement>(null);

  // Échap ferme, et un clic à l'extérieur aussi. Sans cela, le panneau reste
  // accroché à l'écran et il faut viser le bouton pour s'en débarrasser.
  useEffect(() => {
    if (!ouvert) return;

    function auClavier(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOuvert(false);
        declencheur.current?.focus();
      }
    }
    function auClic(event: MouseEvent) {
      if (zone.current && !zone.current.contains(event.target as Node)) setOuvert(false);
    }

    document.addEventListener('keydown', auClavier);
    document.addEventListener('mousedown', auClic);
    return () => {
      document.removeEventListener('keydown', auClavier);
      document.removeEventListener('mousedown', auClic);
    };
  }, [ouvert]);

  return (
    <div
      ref={zone}
      className="fixed bottom-4 right-4 z-30 flex flex-col items-end gap-3 sm:bottom-6 sm:right-6"
      /* Sur iPhone, la barre de navigation du système mange le bas de l'écran :
         sans cette marge, le bouton se retrouve dessous. */
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Le panneau                                                          */}
      {/* ------------------------------------------------------------------ */}
      {ouvert && (
        <div className="w-[min(19rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-xl">
          <div className="border-b border-ink-100 px-4 py-3">
            <p className="text-sm font-bold text-ink-950">Besoin d’aide ?</p>
            <p className="mt-0.5 text-[13px] text-ink-500">{SUPPORT.horaires}</p>
          </div>

          <div className="flex flex-col p-2">
            <Canal
              href={lienWhatsapp()}
              externe
              titre="WhatsApp"
              detail="La réponse la plus rapide"
              icone={
                <path d="M12.04 2a9.9 9.9 0 00-8.5 15l-1.1 4 4.12-1.08A9.9 9.9 0 1012.04 2zm5.76 14.1c-.24.67-1.4 1.28-1.94 1.33-.5.05-1.13.07-1.82-.11a16.6 16.6 0 01-1.65-.61c-2.9-1.25-4.8-4.17-4.95-4.37-.14-.2-1.18-1.57-1.18-3s.75-2.13 1.02-2.42c.27-.29.58-.36.78-.36h.56c.18 0 .42-.07.66.5l.9 2.18c.08.15.13.33.03.53-.1.2-.15.32-.3.5l-.44.51c-.15.15-.3.32-.13.62.17.3.76 1.25 1.63 2.02 1.12 1 2.06 1.3 2.36 1.45.3.15.47.13.64-.08l.92-1.07c.21-.25.39-.2.65-.1l1.86.88c.27.13.45.2.52.31.07.11.07.64-.17 1.3z" />
              }
            />

            <Canal
              href={lienTelephone()}
              titre="Appeler"
              detail={SUPPORT.telephone}
              icone={
                <path d="M4 5c0-.6.4-1 1-1h2.3c.5 0 .9.3 1 .8l.7 2.8c.1.4 0 .8-.4 1l-1.5 1a12 12 0 005.3 5.3l1-1.5c.2-.3.6-.5 1-.4l2.8.7c.5.1.8.5.8 1V19c0 .6-.4 1-1 1h-1C9.6 20 4 14.4 4 6V5z" />
              }
            />

            {/* L'e-mail n'apparaît que s'il est renseigné dans
                src/config/support.ts : une ligne qui mène à une adresse
                inexistante vaut moins que pas de ligne du tout. */}
            {SUPPORT.email ? (
              <Canal
                href={`mailto:${SUPPORT.email}`}
                titre="Écrire un e-mail"
                detail={SUPPORT.email}
                icone={
                  <path d="M3 6.5A1.5 1.5 0 014.5 5h15A1.5 1.5 0 0121 6.5v11a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 17.5v-11zm1.9.5l7.1 5.2L19.1 7H4.9z" />
                }
              />
            ) : null}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Le bouton                                                           */}
      {/* ------------------------------------------------------------------ */}
      <button
        ref={declencheur}
        type="button"
        onClick={() => setOuvert((v) => !v)}
        aria-expanded={ouvert}
        aria-label={ouvert ? 'Fermer l’aide' : 'Contacter le support'}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-500 text-white shadow-glow transition-transform hover:bg-brand-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 active:scale-95"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-7 w-7"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          {ouvert ? (
            <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
          ) : (
            <path
              d="M21 11.5a8.4 8.4 0 01-9 8.4 9 9 0 01-3.3-.7L3 21l1.9-5.1A8.3 8.3 0 013.6 11.5a8.4 8.4 0 018.7-8.4 8.4 8.4 0 018.7 8.4z"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          )}
        </svg>
      </button>
    </div>
  );
}

/** Une ligne du panneau : icône, intitulé, précision. */
function Canal({
  href,
  titre,
  detail,
  icone,
  externe = false,
}: {
  href: string;
  titre: string;
  detail: string;
  icone: React.ReactNode;
  externe?: boolean;
}) {
  return (
    <a
      href={href}
      {...(externe ? { target: '_blank', rel: 'noreferrer' } : {})}
      className="flex items-center gap-3 rounded-xl px-2.5 py-2.5 transition-colors hover:bg-ink-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
          {icone}
        </svg>
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-bold text-ink-950">{titre}</span>
        <span className="block truncate text-[13px] text-ink-500">{detail}</span>
      </span>
    </a>
  );
}
